
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/context/auth-context";
import { SupportTicket } from "@/lib/types/support-tickets";
import { 
  getTicketById, 
  getTicketComments,
  subscribeToTicketComments 
} from "@/lib/services/support-ticket-service";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import TicketDetailsSidebar from "@/components/support/TicketDetailsSidebar";
import TicketCommentList from "@/components/support/TicketCommentList";
import TicketCommentForm from "@/components/support/TicketCommentForm";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RealtimeChannel } from "@supabase/supabase-js";

const TicketDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const isStaff = profile?.role === 'staff' || profile?.role === 'admin' || profile?.role === 'tech';
  
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  
  // Query for ticket
  const {
    data: ticketData,
    isLoading: isLoadingTicket,
    error: ticketError
  } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => getTicketById(id!),
    enabled: !!id && !!profile,
  });
  
  // Query for comments
  const {
    data: comments,
    isLoading: isLoadingComments,
    error: commentsError
  } = useQuery({
    queryKey: ['ticketComments', id],
    queryFn: () => getTicketComments(id!),
    enabled: !!id && !!profile,
  });
  
  // Set up ticket state from data
  useEffect(() => {
    if (ticketData) {
      setTicket(ticketData);
    }
  }, [ticketData]);
  
  // Set up real-time comments subscription
  useEffect(() => {
    if (!id || !profile) return;
    
    let commentsChannel: RealtimeChannel | null = null;
    
    // Subscribe to new comments
    commentsChannel = subscribeToTicketComments(id, (newComment) => {
      // Update comments query cache
      queryClient.setQueryData(['ticketComments', id], (oldData: any) => {
        if (!oldData) return [newComment];
        return [...oldData, newComment];
      });
      
      // Show toast notification
      if (newComment.user_id !== profile.id) {
        toast({
          title: "New Comment",
          description: `${newComment.user_name} added a comment`,
        });
      }
    });
    
    // Cleanup function
    return () => {
      if (commentsChannel) supabase.removeChannel(commentsChannel);
    };
  }, [id, profile, queryClient]);
  
  // Update the ticket in state when updated
  const handleTicketUpdated = (updatedTicket: SupportTicket) => {
    setTicket(updatedTicket);
    
    // Also update the query cache
    queryClient.setQueryData(['ticket', id], updatedTicket);
  };
  
  // Redirect if not authenticated
  useEffect(() => {
    if (profile === null) {
      navigate("/auth/login", { 
        replace: true,
        state: { from: `/support/tickets/${id}` }
      });
    }
  }, [profile, navigate, id]);
  
  // Check if we have permission to view this ticket
  useEffect(() => {
    if (ticket && profile && !isStaff && ticket.created_by !== profile.id) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to view this ticket.",
        variant: "destructive"
      });
      navigate("/support/tickets");
    }
  }, [ticket, profile, isStaff, navigate]);
  
  // Loading state
  if (isLoadingTicket || (!ticket && !ticketError)) {
    return (
      <div className="container max-w-6xl mx-auto py-8">
        <div className="flex items-center mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            className="mr-4"
            onClick={() => navigate("/support/tickets")}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Skeleton className="h-8 w-64" />
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Skeleton className="h-32 mb-4" />
            <Skeleton className="h-64" />
          </div>
          <div>
            <Skeleton className="h-48 mb-4" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </div>
    );
  }
  
  // Error state
  if (ticketError) {
    return (
      <div className="container max-w-6xl mx-auto py-8 text-center">
        <h1 className="text-3xl font-title text-primary mb-4">Ticket Not Found</h1>
        <p className="text-muted-foreground mb-6">
          The ticket you're looking for doesn't exist or you don't have permission to view it.
        </p>
        <Button asChild>
          <Link to="/support/tickets">Back to Tickets</Link>
        </Button>
      </div>
    );
  }
  
  if (!ticket) return null;
  
  return (
    <div className="container max-w-6xl mx-auto py-8">
      <div className="flex items-center mb-6">
        <Button 
          variant="ghost" 
          size="sm" 
          className="mr-4"
          onClick={() => navigate("/support/tickets")}
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to Tickets
        </Button>
        <h1 className="text-2xl font-title text-primary">Ticket #{ticket.id.substring(0, 8)}</h1>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">{ticket.title}</CardTitle>
              <CardDescription>
                Submitted by {ticket.creator_name} • {new Date(ticket.created_at).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="prose max-w-none">
                <p className="whitespace-pre-wrap">{ticket.description || "No description provided."}</p>
              </div>
            </CardContent>
          </Card>
          
          <div className="space-y-4">
            <h2 className="text-xl font-title text-primary">Comments</h2>
            <TicketCommentList 
              comments={comments || []} 
              isLoading={isLoadingComments} 
            />
            <Card>
              <CardContent className="pt-4">
                <TicketCommentForm 
                  ticketId={ticket.id} 
                  isStaff={isStaff}
                />
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div>
          <TicketDetailsSidebar 
            ticket={ticket} 
            onTicketUpdated={handleTicketUpdated} 
          />
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
